/**
 * Indicator Panel
 *
 * Dropdown button in the chart toolbar that lets users:
 * - Browse available indicators by category
 * - Add indicators to the chart
 * - See active indicators with toggle visibility / remove
 * - Edit indicator parameters inline
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { indicatorEngine } from "../../indicators/engine/indicator-engine";
import { ActiveIndicator, IndicatorCategory } from "../../indicators/types/indicator.types";
import { IndicatorListItem } from "../../store/metadata-slice";
import "./IndicatorPanel.scss";

interface IndicatorPanelProps {
  activeIndicators: ActiveIndicator[];
  onAdd: (indicatorId: string) => void;
  onRemove: (instanceId: string) => void;
  onRemoveAll: () => void;
  onToggleVisibility: (instanceId: string) => void;
  onParamsChange: (instanceId: string, params: Record<string, any>) => void;
  colorTheme: string;
  compact?: boolean;
}

const CATEGORIES: { key: IndicatorCategory; label: string }[] = [
  { key: "trend", label: "Trend" },
  { key: "momentum", label: "Momentum" },
  { key: "volatility", label: "Volatility" },
  { key: "volume", label: "Volume" },
  { key: "support_resistance", label: "S/R" },
  { key: "advanced", label: "Advanced" },
  { key: "sessions", label: "Sessions" },
  { key: "strategies", label: "Premium Strategies" },
];

const normalizeIndicatorKey = (value?: string): string =>
  (value || "").trim().toLowerCase().replace(/[_\s-]+/g, " ");

const isUnexpired = (expiryDate?: string): boolean => {
  if (!expiryDate) return false;
  const parsed = new Date(expiryDate);
  if (Number.isNaN(parsed.getTime())) {
    // Do not lock paid indicators on malformed date strings from backend.
    return true;
  }
  return parsed >= new Date();
};

const IndicatorPanel: React.FC<IndicatorPanelProps> = ({
  activeIndicators,
  onAdd,
  onRemove,
  onRemoveAll,
  onToggleVisibility,
  onParamsChange,
  colorTheme,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<IndicatorCategory>("trend");
  const [search, setSearch] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Close on outside click — covers both the button wrapper and the portal dropdown
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPanel = panelRef.current && panelRef.current.contains(target);
      const inPortal = document.getElementById("indicator-dropdown-portal")?.contains(target);
      if (!inPanel && !inPortal) setIsOpen(false);
    };
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  // Recalculate dropdown position whenever it opens or on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dropdownWidth = 320;
      const viewportWidth = window.innerWidth;
      // Prefer left-aligned; if it would overflow right, right-align instead
      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - 8) left = viewportWidth - dropdownWidth - 8;
      if (left < 8) left = 8;
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left,
        width: dropdownWidth,
        zIndex: 99999,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isOpen]);

  const allowedIndicators: string[] = useSelector(
    (state: any) => state.metadata?.indicatorState?.allowedIndicators ?? []
  );
  const indicatorsWithExpiry: { id: string; expiryDate: string }[] = useSelector(
    (state: any) => state.metadata?.indicatorState?.indicatorsWithExpiry ?? []
  );
  const metadataIndicatorList: Array<string | IndicatorListItem> = useSelector(
    (state: any) => state.metadata?.indicatorState?.indicatorList ?? []
  );
  const indicatorExpiryDtMap: Record<string, string> = useSelector(
    (state: any) => state.metadata?.indicatorState?.indicatorExpiryDtMap ?? {}
  );
  const licenseType: string = useSelector(
    (state: any) => state.metadata?.dashboardState?.licenseType ?? ""
  );
  const isDemoLicense = licenseType?.toString().toLowerCase() === "demo";

  // O(1) expiry lookup: indicatorId → ISO date string
  const expiryMap = useMemo(
    () =>
      Object.fromEntries(indicatorsWithExpiry.map((e) => [e.id, e.expiryDate])),
    [indicatorsWithExpiry]
  );

  const rawExpiryMap = useMemo(
    () => new Map<string, string>(Object.entries(indicatorExpiryDtMap || {})),
    [indicatorExpiryDtMap]
  );

  const activeLicensedStrategyIdSet = useMemo(
    () =>
      new Set(
        Array.from(rawExpiryMap.entries())
          .filter(([, expiryDate]) => isUnexpired(expiryDate))
          .map(([id]) => id)
      ),
    [rawExpiryMap]
  );

  const normalizedActiveLicensedStrategyIdSet = useMemo(
    () => new Set(Array.from(activeLicensedStrategyIdSet).map((id) => normalizeIndicatorKey(id))),
    [activeLicensedStrategyIdSet]
  );

  const normalizedMetadataStrategySet = useMemo(
    () =>
      new Set(
        metadataIndicatorList
          .flatMap((item) => {
            if (typeof item === "string") {
              return [normalizeIndicatorKey(item)];
            }
            return [normalizeIndicatorKey(item?.id), normalizeIndicatorKey(item?.name)];
          })
          .filter(Boolean)
      ),
    [metadataIndicatorList]
  );

  const metadataStrategyEntries = useMemo(
    () =>
      metadataIndicatorList
        .map((item) => {
          if (typeof item === "string") {
            return {
              id: "",
              name: item,
              keys: [normalizeIndicatorKey(item)].filter(Boolean),
            };
          }
          const id = item?.id || "";
          const name = item?.name || "";
          return {
            id,
            name,
            keys: [normalizeIndicatorKey(id), normalizeIndicatorKey(name)].filter(Boolean),
          };
        })
        .filter((entry) => entry.keys.length > 0),
    [metadataIndicatorList]
  );

  const isExpired = useCallback(
    (id: string): boolean => {
      const expiryDate = expiryMap[id];
      if (!expiryDate) return false;
      return new Date(expiryDate) < new Date();
    },
    [expiryMap]
  );

  const hasStrategyLicense = useCallback(
    (indicator: { id: string; name: string; shortName: string; category: IndicatorCategory }) => {
      if (indicator.category !== "strategies") return true;
      if (isDemoLicense) return true;

      // Match only by display name / shortName — never by local ID.
      // Local IDs (e.g. "alpha_trade") normalise to strings identical to
      // metadata names ("alpha trade" = "Alpha Trade") and cause false matches.
      const indicatorKeys = [
        normalizeIndicatorKey(indicator.name),
        normalizeIndicatorKey(indicator.shortName),
      ].filter(Boolean);

      const matchedMetadataEntry = metadataStrategyEntries.find((entry) =>
        entry.keys.some((key) => indicatorKeys.includes(key))
      );

      // General license: indicatorExpiryDtMap keys are strategy license IDs.
      // Map local strategy -> metadata indicatorList entry -> metadata ID,
      // then enable only when that ID exists with active (non-expired) license.
      const metadataId = matchedMetadataEntry?.id || "";
      if (!metadataId) return false;

      return (
        activeLicensedStrategyIdSet.has(metadataId) ||
        normalizedActiveLicensedStrategyIdSet.has(normalizeIndicatorKey(metadataId))
      );
    },
    [
      activeLicensedStrategyIdSet,
      isDemoLicense,
      metadataStrategyEntries,
      normalizedActiveLicensedStrategyIdSet,
    ]
  );

  // Apply master-list filter for non-strategy categories.
  const masterListFiltered = useMemo(() => {
    const all = indicatorEngine.getAll();
    return allowedIndicators.length === 0
      ? all
      : all.filter((ind) => ind.category === "strategies" || allowedIndicators.includes(ind.id));
  }, [allowedIndicators]);

  // Strategies are shown strictly from /metadata indicatorList and are
  // enabled/disabled by license checks in hasStrategyLicense.
  // If metadata has no strategies, show none in Premium Strategies tab.
  const allIndicators = useMemo(() => {
    const strategyIndicators = masterListFiltered.filter((ind) => ind.category === "strategies");
    const nonStrategyIndicators = masterListFiltered.filter((ind) => ind.category !== "strategies");

    const metadataStrategies = strategyIndicators.filter((ind) => {
      // Match only on display name / shortName, not on the local internal ID.
      const nameKey = normalizeIndicatorKey(ind.name);
      const shortNameKey = normalizeIndicatorKey(ind.shortName);
      return (
        normalizedMetadataStrategySet.has(nameKey) ||
        normalizedMetadataStrategySet.has(shortNameKey)
      );
    });

    return [...nonStrategyIndicators, ...metadataStrategies];
  }, [masterListFiltered, normalizedMetadataStrategySet]);

  const categoryIndicators = allIndicators.filter(ind => ind.category === selectedCategory);
  const filteredIndicators = (search.trim()
    ? allIndicators.filter(
        ind =>
          ind.name.toLowerCase().includes(search.toLowerCase()) ||
          ind.shortName.toLowerCase().includes(search.toLowerCase())
      )
    : categoryIndicators
  ).slice().sort((a, b) => a.name.localeCompare(b.name));

  const handleAdd = (indicatorId: string) => {
    onAdd(indicatorId);
    // Stay open so user can add more
  };

  return (
    <div className={`indicator-panel-wrapper ${colorTheme}`} ref={panelRef}>
      <button
        ref={btnRef}
        className="indicator-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Indicators"
      >
        <span className="indicator-icon">ƒ</span>
        <span className="indicator-label">Indicator</span>
        {activeIndicators.length > 0 && (
          <span className="indicator-count">{activeIndicators.length}</span>
        )}
        <span className="dropdown-arrow">▼</span>
      </button>

      {isOpen && createPortal(
        // eslint-disable-next-line react/forbid-dom-props
        <div id="indicator-dropdown-portal" className={`indicator-dropdown indicator-dropdown-portal ${colorTheme}`} style={dropdownStyle}>
          {/* Header: search + close */}
          <div className="indicator-dropdown-header">
            <div className="indicator-search">
              <input
                type="text"
                placeholder="Search indicators..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <button
              className="indicator-close-btn"
              onClick={() => setIsOpen(false)}
              title="Close"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {/* Browse */}
          <div className="indicator-browse">
              {!search.trim() && (
                <div className="category-tabs">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.key}
                      className={`cat-btn ${selectedCategory === cat.key ? "active" : ""}`}
                      onClick={() => setSelectedCategory(cat.key)}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="indicator-list">
                {filteredIndicators.map(ind => {
                  const instanceCount = activeIndicators.filter(a => a.indicatorId === ind.id).length;
                  const expired = isExpired(ind.id);
                  const unlicensed = !hasStrategyLicense(ind as any);
                  const disabled = expired || unlicensed;
                  const disabledReason = expired
                    ? "License expired for this indicator"
                    : unlicensed
                      ? "License not available for this indicator"
                      : ind.name;
                  return (
                    <div
                      key={ind.id}
                      className={`indicator-item${disabled ? " indicator-item--disabled" : ""}`}
                      onClick={() => !disabled && handleAdd(ind.id)}
                      title={disabledReason}
                    >
                      <div className="indicator-item-info">
                        <span className="indicator-item-name">{ind.name}</span>
                        <span className="indicator-item-badge">{ind.pane === "overlay" ? "Overlay" : "Sub"}</span>
                        {!disabled && instanceCount > 0 && (
                          <span className="indicator-instance-count" title={`${instanceCount} active instance${instanceCount > 1 ? 's' : ''}`}>
                            {instanceCount}
                          </span>
                        )}
                      </div>
                      {disabled ? (
                        <span className="indicator-item-lock" title={expired ? "License expired" : "License not available"}>
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                          </svg>
                        </span>
                      ) : (
                        <span className="indicator-item-add">+</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default IndicatorPanel;
